import React, { useCallback, useMemo, useState } from 'react'
import cx from 'classnames'
import PropTypes, { any, arrayOf, bool, func, node, objectOf, string } from 'prop-types'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

import { FilterIcon, MinusIcon, PlusIcon, SearchIcon } from '@wf-sl/common-icons'

import { IconIndication } from '../icon-indication'
import virtualListPropType from '../virtual-list/virtual-list-prop-type'
import AssignmentEditorList from './assignment-editor-list'

import styles from './assignment-editor.module.css'

const propTypes = {
    addItemIcon: node,
    addItemsOnTop: bool,
    className: string,
    createListItemIcon: func,
    customItemSort: func,
    // eslint-disable-next-line react/forbid-prop-types
    editedGroup: objectOf(PropTypes.any).isRequired,
    emptyLeftListText: string,
    emptyRightListText: string,
    enableItemForUnassignment: func,
    filterBarClassName: string,
    filterBy: string,
    groupsFilterClassName: string,
    groupsFilterIcon: node,
    groupsPlaceholderText: string,
    leftButtonText: string,
    leftListHeaderText: string,
    listClassName: string,
    listItemIcon: node,
    mapObjectGroupToItemGroup: func.isRequired,
    mapObjectToItem: func.isRequired,
    noResultsText: string,
    onChange: func,
    removeHintText: string,
    removeItemIcon: node,
    rightButtonText: string,
    rightListHeaderText: string,
    searchFilterClassName: string,
    searchPlaceholderText: string,
    unfilteredGroups: arrayOf(objectOf(any)).isRequired,
    unfilteredItems: arrayOf(objectOf(any)).isRequired,
    virtual: virtualListPropType
}

const defaultProps = {
    addItemIcon: (
        <PlusIcon
            width={22}
            className={cx(styles['assignment-editor-action-icon'], 't3sel-assignment-editor-list-row-action')}
        />
    ),
    addItemsOnTop: true,
    emptyLeftListText: '',
    emptyRightListText: '',
    enableItemForUnassignment: () => true,
    filterBy: 'all',
    groupsFilterIcon: <FilterIcon width={16} />,
    groupsPlaceholderText: '',
    leftButtonText: '',
    leftListHeaderText: '',
    noResultsText: '',
    onChange: Function.prototype,
    removeItemIcon: (
        <MinusIcon
            width={22}
            className={cx(styles['assignment-editor-action-icon'], 't3sel-assignment-editor-list-row-action')}
        />
    ),
    rightButtonText: '',
    rightListHeaderText: '',
    searchPlaceholderText: ''
}

/**
 * Component for managing group members
 *
 * Usage:
 *
 * @example
 * <AssignmentEditor editedGroup={group1},
 *                   unfilteredGroups={[group1, group2]},
 *                   unfilteredItems={[item1, item2, item3]},
 *                   mapObjectToItem={(object) => {
 *                       return {
 *                         id: object.objectId,
 *                         name: object.name,
 *                         appendix: object.number (not mandatory),
 *                         color: object.color (not mandatory)
 *                       }
 *                   }}
 *                   mapObjectGroupToItemGroup={(objectgroup) => {
 *                       return {
 *                         id: objectgroup.groupId,
 *                         name: objectgroup.groupName,
 *                         // maps available items to internal structure:
 *                         items: objectgroup.items.map(mapObjectToItem)
 *                       }
 *                   }}
 *                   onChange={(value) => {callbackFn(value)}} />

 * @param {Object}      [editedGroup]                   - currently edited group object.
 * @param {Object[]}    [unfilteredGroups]              - List of all groups for filtering items.
 * @param {Object[]}    [unfilteredItems]               - List of all items for filtering.
 * @param {Function}    [mapObjectToItem]               - Callback for mapping one object to an item.
 * @param {Function}    [mapObjectGroupToItemGroup]     - Callback for mapping one objectgroup to an itemgroup.
 * @param {Function}    [onChange]                      - Callback when one of the list changes.
 * @param {Function}    [enableItemForUnassignment]     - A predicate for marking an item as unassignable.
 * @param {string}      [className]                     - Additional css className for wrapper element.
 * @param {string}      [listClassName]                 - Additional css className for list element.
 * @param {string}      [filterBy='name']               - Filter by string.
 * @param {node}        [groupFilterIcon=<FilterIcon/>] - Groups dropdown filter icon.
 * @param {node}        [listItemIcon]                  - List item icon
 * @param {node}        [addItemIcon=<PlusIcon/>]       - Add item icon
 * @param {node}        [removeItemIcon=<CrossIcon/>]   - Remove item icon
 * @param {Function}    [createListItemIcon]            - Custom function for creating list item icon
 * @param {string}      [noResultsText='']              - Placeholder text for no search results
 * @param {string}      [emptyLeftListText='']          - Placeholder text for empty left list indicator
 * @param {string}      [emptyRightListText='']         - Placeholder text for empty right list indicator
 * @param {string}      [leftListHeaderText='']         - Placeholder text for left list header
 * @param {string}      [rightListHeaderText='']        - Placeholder text for right list header
 * @param {string}      [searchPlaceholderText='']      - Placeholder text for the filter textfield.
 * @param {string}      [groupsPlaceholderText='']      - Placeholder text for groups select default option
 * @param {string}      [leftButtonText='']             - Placeholder text for left right list button
 * @param {string}      [rightButtonText='']            - Placeholder text for right right list button
 * @param {VirtualList} [virtual]                       - settings to initialize a virtual list
 * @param {bool}        [addItemsOnTop]                 - newly assigned items should be placed on the top of the list?
 *
 * @returns {ReactElement}
 */
const AssignmentEditor = ({
    addItemIcon,
    addItemsOnTop,
    className,
    createListItemIcon,
    customItemSort,
    editedGroup,
    emptyLeftListText,
    emptyRightListText,
    enableItemForUnassignment,
    filterBarClassName,
    filterBy,
    groupsFilterClassName,
    groupsFilterIcon,
    groupsPlaceholderText,
    leftButtonText,
    leftListHeaderText,
    listClassName,
    listItemIcon,
    mapObjectGroupToItemGroup,
    mapObjectToItem,
    noResultsText,
    onChange,
    removeHintText,
    removeItemIcon,
    rightButtonText,
    rightListHeaderText,
    searchFilterClassName,
    searchPlaceholderText,
    unfilteredGroups,
    unfilteredItems,
    virtual
}) => {
    const initialItems = useMemo(() => {
        const selectedIds = mapObjectGroupToItemGroup(editedGroup).items.map(obj => obj.id)
        return unfilteredItems.map(mapObjectToItem).filter(obj => selectedIds.indexOf(obj.id) === -1)
    }, [unfilteredItems, editedGroup, mapObjectToItem, mapObjectGroupToItemGroup])

    const groups = useMemo(
        () =>
            unfilteredGroups
                .map(mapObjectGroupToItemGroup)
                .filter(group => editedGroup && group.id !== mapObjectGroupToItemGroup(editedGroup).id),
        [unfilteredGroups, editedGroup, mapObjectGroupToItemGroup]
    )

    const initiallyAddedItems = useMemo(
        () => mapObjectGroupToItemGroup(editedGroup).items || [],
        [editedGroup, mapObjectGroupToItemGroup]
    )

    const [added, setAdded] = useState(initiallyAddedItems.slice(0))
    const [available, setAvailable] = useState(initialItems.slice(0))

    const updateState = useCallback(
        (newAvailable, newAdded) => {
            const newAssignments = newAdded.filter(n => !initiallyAddedItems.some(i => i.id === n.id))
            const removedAssignments = initiallyAddedItems.filter(n => !newAdded.some(i => i.id === n.id))

            setAvailable(newAvailable)
            setAdded(newAdded)

            onChange({
                assignments: newAdded,
                newAssignments,
                removedAssignments
            })
        },
        [initiallyAddedItems, onChange]
    )

    /**
     * Assign one item
     * @param {object} item
     */
    const addItem = useCallback(
        item => {
            const newAdded = addItemsOnTop ? [item, ...added] : [...added, item]

            updateState(
                available.filter(obj => obj.id !== item.id),
                newAdded
            )
        },
        [updateState, available, added, addItemsOnTop]
    )

    /**
     * Remove one item from assigned
     * @param {object} item
     */
    const removeItem = useCallback(
        item => {
            if (!enableItemForUnassignment(item)) {
                return
            }

            const newAvailable = addItemsOnTop ? [item, ...available] : [...available, item]

            updateState(
                newAvailable,
                added.filter(obj => obj.id !== item.id)
            )
        },
        [updateState, available, added, addItemsOnTop, enableItemForUnassignment]
    )

    const onMoveItemAvailable = useCallback(
        dragIdx => {
            setAvailable(available.filter(card => card.id === dragIdx))
        },
        [available]
    )

    const onMoveItemAdded = useCallback(
        dragIdx => {
            setAdded(added.filter(card => card.id === dragIdx))
        },
        [added]
    )

    /**
     * Assign all available/filtered items
     */
    const addAll = addedItems => {
        const availableItems = available.filter(item => !addedItems.some(added => added.id === item.id))
        updateState(availableItems, added.concat(addedItems))
    }

    /**
     * Remove all assigned items
     */
    const removeAll = items => {
        const removedItems = items.filter(enableItemForUnassignment)
        const addedItems = added.filter(item => !removedItems.some(added => added.id === item.id))
        updateState(available.concat(removedItems), addedItems)
    }

    const NoResultsPlaceholder = () => (
        <div className={cx(styles['empty-list-item'])}>
            <IconIndication icon={<SearchIcon />}>{noResultsText}</IconIndication>
        </div>
    )
    const EmptyAvailableListPlaceholder = () => (
        <div className={cx(styles['empty-list-item'])}>
            <IconIndication icon={<SearchIcon />}>{emptyLeftListText}</IconIndication>
        </div>
    )
    const EmptyAddedListPlaceholder = () => (
        <div className={cx(styles['empty-list-item'])}>
            <IconIndication icon={<PlusIcon />}>{emptyRightListText}</IconIndication>
        </div>
    )

    return (
        <div className={cx(styles['assignment-editor'], className)}>
            <AssignmentEditorList
                listId={0}
                allItems={available}
                className="t3sel-assignment-editor-list-left"
                emptyPlaceholder={<EmptyAvailableListPlaceholder />}
                globalAction={addAll}
                globalActionText={leftButtonText}
                groupsFilterIcon={groupsFilterIcon}
                groupsPlaceholder={groupsPlaceholderText}
                header={leftListHeaderText}
                noResultsPlaceholder={<NoResultsPlaceholder />}
                onMoveItem={onMoveItemAvailable}
                onClickActionIcon={addItem}
                actionIcon={addItemIcon}
                placeholder={searchPlaceholderText}
                {...{
                    createListItemIcon,
                    customItemSort,
                    filterBarClassName,
                    filterBy,
                    groups,
                    groupsFilterClassName,
                    listClassName,
                    listItemIcon,
                    searchFilterClassName,
                    virtual
                }}
            />
            <AssignmentEditorList
                listId={1}
                allItems={added}
                className="t3sel-assignment-editor-list-right"
                emptyPlaceholder={<EmptyAddedListPlaceholder />}
                globalAction={removeAll}
                globalActionText={rightButtonText}
                groupsFilterIcon={groupsFilterIcon}
                groupsPlaceholder={groupsPlaceholderText}
                header={rightListHeaderText}
                noResultsPlaceholder={<NoResultsPlaceholder />}
                onMoveItem={onMoveItemAdded}
                onClickActionIcon={removeItem}
                actionIcon={removeItemIcon}
                placeholder={searchPlaceholderText}
                {...{
                    createListItemIcon,
                    customItemSort,
                    enableItemForUnassignment,
                    filterBarClassName,
                    filterBy,
                    groups,
                    groupsFilterClassName,
                    listClassName,
                    listItemIcon,
                    removeHintText,
                    searchFilterClassName,
                    virtual
                }}
            />
        </div>
    )
}

AssignmentEditor.propTypes = propTypes
AssignmentEditor.defaultProps = defaultProps

function AssignmentEditorWithContext(props) {
    return (
        <DndProvider backend={HTML5Backend}>
            <AssignmentEditor {...props} />
        </DndProvider>
    )
}

export { AssignmentEditor as AssignmentEditorNoContext }
export default AssignmentEditorWithContext
